import { useState, useEffect, useRef, useMemo } from 'react';
import { Heading, Text, Badge, LayerCard } from '@bq/web-components';
import manifest from '@bq/web-tokens/dist/crd-manifest.json';
import styles from './LayerZoomSection.module.css';

interface CrdEntry {
  crd_id: string;
  crd_layer: string;
  name: string;
}

const LAYER_META = [
  {
    layer: 'r7',
    name: 'R7 — Space',
    description:
      'The entire application environment. Orchestrates surfaces, routing, and global state. This is where the user lives.',
    fallback: ['App Shell', 'Router', 'Global Providers'],
  },
  {
    layer: 'r6',
    name: 'R6 — Surfaces',
    description:
      'Top-level layout containers. Each surface owns a navigation context, header/footer chrome, and renders pages via an Outlet.',
    fallback: ['MarketingSurface', 'ShowcaseSurface'],
  },
  {
    layer: 'r5',
    name: 'R5 — Pages',
    description:
      'Full-page compositions that stack R4 sections vertically. Each page corresponds to a route and composes the user\'s journey.',
    fallback: ['LandingPage', 'ShowcasePage'],
  },
  {
    layer: 'r4',
    name: 'R4 — Sections',
    description:
      'Major content blocks that compose R3 components into cohesive regions. Each section is a self-contained story beat.',
    fallback: ['HeroSection', 'FeaturesSection', 'PricingSection'],
  },
  {
    layer: 'r3',
    name: 'R3 — Components',
    description:
      'Reusable UI components built from R2 elements. Components carry domain meaning — a card, a navbar, a form.',
    fallback: ['FeatureCard', 'PricingCard', 'Navbar'],
  },
  {
    layer: 'r2',
    name: 'R2 — Elements',
    description:
      'Atomic UI primitives. Each element wraps a single HTML concept with CRD tokens — no business logic.',
    fallback: ['Heading', 'Text', 'Button', 'Image'],
  },
  {
    layer: 'r1',
    name: 'R1 — Primitives',
    description:
      'Design tokens — the raw values that feed every layer above. Colors, type scales, spacing, borders, shadows.',
    fallback: ['--bq-p500', '--bq-ff-primary', '--bq-size2'],
  },
];

function buildLayers() {
  const entries = manifest as CrdEntry[];
  const byLayer = new Map<string, string[]>();
  for (const e of entries) {
    const names = byLayer.get(e.crd_layer) ?? [];
    names.push(e.name);
    byLayer.set(e.crd_layer, names);
  }
  return LAYER_META.map((l) => ({
    layer: l.layer,
    name: l.name,
    description: l.description,
    examples: byLayer.get(l.layer) ?? l.fallback,
  }));
}

export function LayerZoomSection() {
  const layers = useMemo(buildLayers, []);
  const [activeIndex, setActiveIndex] = useState(-1);
  const sentinelsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-index'));
            if (!Number.isNaN(idx)) {
              setActiveIndex(idx);
            }
          }
        }
      },
      {
        rootMargin: '-40% 0px -40% 0px',
        threshold: 0,
      },
    );

    for (const el of sentinelsRef.current) {
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="zoom"
      data-crd-id="r4.bq-layer-zoom-section"
      data-crd-layer="r4"
      className={styles.section}
    >
      <div className={styles.intro}>
        <Badge variant="default">Feature #3</Badge>
        <Heading level={2} as={2} align="center">
          Layer Zoom
        </Heading>
        <Text size="r1" align="center" color="n500">
          Scroll down through the seven CRD layers — from the abstract Space
          all the way to raw Primitives. Each layer only references the layers
          below it.
        </Text>
      </div>

      <div className={styles.scrollContainer}>
        <div className={styles.viewport}>
          {layers.map((l, i) => (
            <LayerCard
              key={l.layer}
              layer={l.layer}
              name={l.name}
              description={l.description}
              examples={l.examples}
              active={activeIndex === i}
            />
          ))}
        </div>

        <div className={styles.sentinels}>
          {layers.map((_, i) => (
            <div
              key={i}
              ref={(el) => { sentinelsRef.current[i] = el; }}
              data-index={i}
              className={styles.sentinel}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
